// 0-100 composite scores for the grouped comparison table. These deliberately use
// transparent, fixed rules rather than cross-sectional ranking, so the same security gets
// the same score regardless of what it is compared against.

import type { CompareSide, RiskMetrics, WatchlistItem } from "../types";
import { cagr } from "./format";

const clamp = (value: number) => Math.max(0, Math.min(100, value));

function average(values: Array<number | null>): number | null {
  const usable = values.filter((value): value is number => value != null && Number.isFinite(value));
  if (usable.length === 0) return null;
  return Math.round(usable.reduce((sum, value) => sum + value, 0) / usable.length);
}

function linear(value: number | null | undefined, low: number, high: number): number | null {
  if (value == null || !Number.isFinite(value) || high === low) return null;
  return clamp(((value - low) / (high - low)) * 100);
}

/** Blends 1Y return with annualized 3Y, 5Y, 10Y, and full-history returns when present.
    -20% maps to 0, 0% to 50, and +20% to 100. YTD is excluded because its elapsed
    period changes through the year. */
export function returnsScore(item: CompareSide): number | null {
  return average([
    linear(item.returns?.one_year, -20, 20),
    linear(cagr(item.returns?.three_year, 3), -20, 20),
    linear(cagr(item.returns?.five_year, 5), -20, 20),
    linear(item.analytics?.period_returns?.ten_year?.value, -20, 20),
    linear(item.analytics?.period_returns?.all_time?.value, -20, 20),
  ]);
}

/** Lower volatility and shallower maximum drawdown score higher. Beta is descriptive and
    intentionally excluded because neither a high nor low beta is universally better. */
export function riskScore(risk: RiskMetrics | null | undefined): number | null {
  return average([
    risk?.volatility == null ? null : 100 - (linear(risk.volatility, 10, 40) ?? 0),
    risk?.max_drawdown == null
      ? null
      : 100 - (linear(Math.abs(risk.max_drawdown), 10, 60) ?? 0),
  ]);
}

/** Blends all five displayed risk-adjusted ratios. For Omega, 1 (gains equal losses)
    maps to 50 and 1.5 maps to 100; the other ratios reach 50 at their green threshold. */
export function ratiosScore(risk: RiskMetrics | null | undefined): number | null {
  return average([
    linear(risk?.sharpe, 0, 2),
    linear(risk?.sortino, 0, 3),
    linear(risk?.omega, 0.5, 1.5),
    linear(risk?.calmar, 0, 2),
    linear(risk?.martin, 0, 4),
  ]);
}

/** A zero-cost fund scores 100; a net expense ratio of 1% or more scores zero. */
export function fundCostScore(item: WatchlistItem): number | null {
  if (item.expense_ratio == null) return null;
  return Math.round(100 - (linear(item.expense_ratio * 100, 0, 1) ?? 100));
}

/** A single 0-100 verdict combining the group scores into one figure. It is an equal-weight
    average of whichever group scores are available (returns, fund cost, risk, risk-adjusted
    ratios), computed against the same risk window the groups use. Trend measures
    are descriptive and excluded from the quality score. */
export function overallScore(item: CompareSide, risk: RiskMetrics | null | undefined): number | null {
  return average([
    returnsScore(item),
    item.expense_ratio == null ? null : fundCostScore(item),
    riskScore(risk),
    ratiosScore(risk),
  ]);
}


/** A pair-relative valuation score, deliberately separate from the overall quality score.
    Uses only shared positive P/E, P/B, P/S, P/CF for the same valuation kind. Forward
    P/E and PEG are estimates and excluded. Equal ratios give 50; lower ratios score higher.
    Two shared measures are required to avoid a composite based on a single ratio. */
export function valuationScores(a: CompareSide, b: CompareSide): {
  a: number | null; b: number | null; metrics: string[];
} {
  const empty = { a: null, b: null, metrics: [] };
  if (!a.valuation_kind || a.valuation_kind !== b.valuation_kind) return empty;
  const fields = [
    ["price_to_earnings", "P/E"], ["price_to_book", "P/B"], ["price_to_sales", "P/S"],
    ["price_to_cash_flow", "P/CF"],
  ] as const;
  const scores: number[] = [];
  const metrics: string[] = [];
  for (const [field, label] of fields) {
    const av = a[field];
    const bv = b[field];
    if (av == null || bv == null || !Number.isFinite(av) || !Number.isFinite(bv) || av <= 0 || bv <= 0) continue;
    // Scale first so even very large finite ratios cannot overflow their sum.
    const scale = Math.max(av, bv);
    scores.push(100 * (bv / scale) / (av / scale + bv / scale));
    metrics.push(label);
  }
  if (scores.length < 2) return { ...empty, metrics };
  const score = Math.round(scores.reduce((sum, value) => sum + value, 0) / scores.length);
  return { a: score, b: 100 - score, metrics };
}
